import { useContext, useMemo, useState, type FC } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDreamupVideoGeneration } from '@wix/da-papi-types';
import { DATE_FORMATS, formatDate } from '@wix/da-i18next/pkg/time/functions';
import { getModelPropertiesById } from '../../ModelProperties';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import VideoSound from '@wix/da-ds/pkg/Icons/12x12/VideoSound';
import DownloadIcon from '@wix/da-ds/pkg/Icons/24x24/Download';
import StudioStashIcon from '@wix/da-ds/pkg/Icons/24x24/StudioStash';
import CopyIcon from '@wix/da-ds/pkg/Icons/16x16/Copy';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import UpgradeToCoreModal from '../../../../modals/UpgradeToCoreModal';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useCopyToClipboard } from '@wix/da-shared-react/pkg/utils/hooks/useCopyToClipboard';
import VideoCreatorModeration from '../VideoCreatorModeration';

import s from './VideoFile.scss';
import sParent from '../VideoCreatorFiles.scss';

export interface Props {
  videoGeneration: PapiDreamupVideoGeneration;
}

const VideoFile: FC<Props> = ({ videoGeneration }) => {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const [isUpgradeToCoreModalOpen, setIsUpgradeToCoreModalOpen] =
    useState(false);
  const wasModerated = videoGeneration.status === 'rejected';

  const model = useMemo(() => {
    return getModelPropertiesById(videoGeneration.modelId);
  }, [videoGeneration.modelId]);

  const copyPromptToClipboard = useCopyToClipboard(
    videoGeneration.prompt || ''
  );

  const downloadFile = () => {
    // Create an <a> element
    const a = document.createElement('a');
    a.href = videoGeneration.generatedContent?.url;
    a.download = videoGeneration.generatedContent?.url;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const moveToDrafts = () => {
    alert('TODO');
  };

  return (
    <div className={cn(s['root'], sParent['file-item'])}>
      <div className={s['preview']}>
        {wasModerated && (
          <VideoCreatorModeration
            className={s['moderated']}
            companyName={t(`studio.videoCreator.companyName.${model.type}`)}
          />
        )}
        {!wasModerated && (
          <video autoPlay loop muted width="100%" height="319">
            <source
              src={videoGeneration.generatedContent?.url}
              type="video/mp4"
            />
          </video>
        )}
      </div>
      <div className={s['details']}>
        <div className={s['prompt']}>
          <span className={s['prompt-text']}>{videoGeneration.prompt}</span>
          <IconButton
            className={s['copy-icon']}
            icon={CopyIcon}
            size={'small'}
            title={t('studio.videoCreator.files.copy')}
            aria-label={t('studio.videoCreator.files.copy')}
            onClick={copyPromptToClipboard}
          />
        </div>
        <div className={s['properties']}>
          <div className={cn(s['model-name'], s['property'])}>
            {t(`studio.videoCreator.modelName.${model.type}`)}
          </div>
          <div className={cn(s['aspect-ratio'], s['property'])}>
            {videoGeneration.aspectRatio}
          </div>
          <div className={cn(s['duration'], s['property'])}>
            {videoGeneration.duration}s
          </div>
          {videoGeneration.sound && (
            <div className={cn(s['sound'], s['property'])}>
              <IconButton
                icon={VideoSound}
                size={'small'}
                title={t('muro.tool.name.erase')}
                aria-label={t('muro.tool.name.erase')}
                className={s['sound-icon']}
              />
            </div>
          )}
        </div>

        {!wasModerated && (
          <div className={s['download']}>
            <TextButton
              className={s['download-button']}
              variant="main"
              size="small"
              onClick={downloadFile}
            >
              <DownloadIcon
                size={IconSize.SMALL}
                disableDefaultColors
                className={s['download-icon']}
              />
              {t('studio.videoCreator.files.download')}
            </TextButton>
          </div>
        )}

        {!wasModerated && (
          <div className={s['save']}>
            <TextButton
              className={s['save-button']}
              variant="main"
              size="small"
              onClick={() => {
                if (hasCoreMembership) {
                  moveToDrafts();
                } else {
                  setIsUpgradeToCoreModalOpen(true);
                }
              }}
            >
              <StudioStashIcon
                size={IconSize.SMALL}
                disableDefaultColors
                className={s['save-icon']}
              />
              {t('studio.videoCreator.files.save')}
            </TextButton>
          </div>
        )}

        <div className={s['created-at']}>
          <span className={s['created-date']}>
            {formatDate(videoGeneration.createdAt, DATE_FORMATS.Date.Short)}
          </span>
          <span className={s['created-time']}>
            {formatDate(
              videoGeneration.createdAt,
              DATE_FORMATS.Time.HourMinute
            )}
          </span>
        </div>
      </div>

      <UpgradeToCoreModal
        isOpen={isUpgradeToCoreModalOpen}
        onRequestClose={() => setIsUpgradeToCoreModalOpen(false)}
        onCancel={() => setIsUpgradeToCoreModalOpen(false)}
        onConfirm={() => setIsUpgradeToCoreModalOpen(false)}
      />
    </div>
  );
};

VideoFile.displayName = 'VideoFile';
export default VideoFile;
